(() => {
  const instances = new Map();
  function initialise(root) {
    if (instances.has(root)) return;
    const header = root.querySelector('[data-new-header]');
    if (!header) return;
    const lifecycle = new AbortController();
    const listen = (target, type, action) => target.addEventListener(type, action, { signal: lifecycle.signal });
    const scrim = root.querySelector('.nh-scrim');
    const triggers = [...header.querySelectorAll('.nh-nav-item[aria-controls]')];
    const panels = [...header.querySelectorAll('.nh-panel')];
    const drawer = root.querySelector('.nh-drawer');
    const menu = header.querySelector('.nh-menu-button');
    const query = header.querySelector('[name="q"]');
    const results = header.querySelector('.nh-search-results');
    let active = null, timer, hoverTimer, openedByHover = false, searchController, requestNumber = 0, suppressFocus = false;
    const hoverMedia = matchMedia('(min-width:1080px) and (hover:hover) and (pointer:fine)');
    const closePanels = (focus = false) => {
      clearTimeout(hoverTimer);
      openedByHover = false;
      const previous = active;
      triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
      panels.forEach(p => p.hidden = true);
      scrim.hidden = true;
      active = null;
      if (focus) previous?.focus();
    };
    const closeSearch = () => {
      clearTimeout(timer);
      searchController?.abort();
      requestNumber++;
      results.hidden = true;
      query.setAttribute('aria-expanded', 'false');
      results.removeAttribute('aria-busy');
    };
    const openPanel = (trigger, fromHover = false) => {
      clearTimeout(hoverTimer);
      if (active === trigger) return;
      closePanels(); closeSearch();
      active = trigger;
      openedByHover = fromHover;
      trigger.setAttribute('aria-expanded', 'true');
      panels.find(p => p.id === trigger.getAttribute('aria-controls')).hidden = false;
      scrim.hidden = false;
    };
    const cancelHoverClose = () => clearTimeout(hoverTimer);
    const scheduleHoverClose = e => {
      if (!hoverMedia.matches || e.pointerType === 'touch') return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => {
        const panel = panels.find(p => p.id === active?.getAttribute('aria-controls'));
        if (!panel?.contains(document.activeElement)) closePanels();
      }, 220);
    };
    triggers.forEach(trigger => {
      listen(trigger, 'pointerenter', e => {
        if (hoverMedia.matches && e.pointerType !== 'touch') openPanel(trigger, true);
      });
      listen(trigger, 'pointerleave', scheduleHoverClose);
      listen(trigger, 'click', e => {
        if (active === trigger && openedByHover && e.detail > 0) {
          cancelHoverClose(); openedByHover = false; return;
        }
        if (active === trigger) closePanels();
        else openPanel(trigger);
      });
    });
    panels.forEach(panel => {
      listen(panel, 'pointerenter', cancelHoverClose);
      listen(panel, 'pointerleave', scheduleHoverClose);
    });
    listen(hoverMedia, 'change', () => closePanels());
    header.querySelectorAll('.nh-panel-close').forEach(b => listen(b, 'click', () => closePanels(true)));
    listen(scrim, 'click', () => closePanels());
    listen(document, 'keydown', e => {
      if (e.key !== 'Escape') return;
      if (!results.hidden) {
        closeSearch(); suppressFocus = true; query.focus(); suppressFocus = false;
      } else closePanels(true);
    });
    const dismissOutside = target => {
      if (!header.contains(target)) closePanels();
      if (!root.querySelector('.nh-search-wrap').contains(target)) closeSearch();
    };
    listen(document, 'click', e => dismissOutside(e.target));
    listen(document, 'focusin', e => dismissOutside(e.target));
    listen(menu, 'click', () => {
      closePanels(); closeSearch(); drawer.showModal(); menu.setAttribute('aria-expanded', 'true');
    });
    listen(drawer.querySelector('.nh-drawer-close'), 'click', () => drawer.close());
    listen(drawer, 'close', () => {
      menu.setAttribute('aria-expanded', 'false');
      if (getComputedStyle(menu).display !== 'none') menu.focus();
    });
    listen(drawer, 'click', e => {
      if (e.target !== drawer) return;
      const r = drawer.getBoundingClientRect();
      if (e.clientX > r.right || e.clientX < r.left) drawer.close();
    });
    const media = matchMedia('(max-width:1079px)');
    const placeholder = () => { query.placeholder = media.matches ? header.dataset.placeholderSmall : header.dataset.placeholderWide; };
    placeholder();
    listen(media, 'change', () => { closePanels(); closeSearch(); if (drawer.open) drawer.close(); placeholder(); });
    async function search() {
      const term = query.value.trim();
      closeSearch();
      if (!term) return;
      const number = requestNumber;
      searchController = new AbortController();
      results.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(header.dataset.searchUrl, location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('section_id', 'snk-header-search');
        const response = await fetch(url, { signal: searchController.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = parsed.querySelector('[data-snk-search-results]');
        if (!content || number !== requestNumber || query.value.trim() !== term) return;
        results.replaceChildren(...content.childNodes);
        results.hidden = false;
        query.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (error.name !== 'AbortError' && number === requestNumber) closeSearch();
      } finally {
        if (number === requestNumber) results.removeAttribute('aria-busy');
      }
    }
    listen(query, 'input', () => { closePanels(); closeSearch(); timer = setTimeout(search, 180); });
    listen(query, 'focus', () => { closePanels(); if (query.value.trim() && !suppressFocus) search(); });
    listen(query, 'keydown', e => {
      if (e.key === 'ArrowDown' && !results.hidden) { e.preventDefault(); results.querySelector('a')?.focus(); }
    });
    listen(results, 'keydown', e => {
      if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;
      const links = [...results.querySelectorAll('a')], index = links.indexOf(document.activeElement);
      if (index < 0) return;
      e.preventDefault();
      if (e.key === 'ArrowUp' && index === 0) {
        suppressFocus = true; query.focus(); suppressFocus = false;
      } else links[Math.max(0, Math.min(links.length - 1, index + (e.key === 'ArrowDown' ? 1 : -1)))]?.focus();
    });
    listen(header.querySelector('.nh-search'), 'submit', e => {
      if (!query.value.trim()) { e.preventDefault(); query.focus(); }
    });
    const updateCount = cart => {
      const count = cart?.item_count ?? cart?.items?.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      if (Number.isFinite(count)) root.querySelectorAll('[data-snk-cart-count]').forEach(el => { el.textContent = String(count); });
    };
    listen(document, 'snk:cart-updated', e => updateCount(e.detail?.cart));
    const nativeCartUpdate = (event, payload) => updateCount(payload?.cartItemData);
    window.jQuery?.(window).on('cc-cart-updated.snkHeader', nativeCartUpdate);
    root.querySelectorAll('.toggle-cart-summary').forEach(b => listen(b, 'click', () => { closePanels(); closeSearch(); }));
    instances.set(root, () => {
      closeSearch(); closePanels(); if (drawer.open) drawer.close();
      lifecycle.abort(); window.jQuery?.(window).off('cc-cart-updated.snkHeader', nativeCartUpdate); instances.delete(root);
    });
  }
  const scan = () => document.querySelectorAll('[data-snk-header-component]').forEach(initialise);
  scan();
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', e => {
    for (const [root, destroy] of instances) if (e.target.contains(root)) destroy();
  });
})();
